/**
 * Cinematic Banner — One-Shot Zoom-In Reveal + Hover Enhancement
 *
 * Animation intent:
 *   The image starts slightly zoomed out (0.8). The moment the section
 *   enters the viewport the image lerps to full size (1.05) and stays
 *   there permanently. The animation plays ONCE and never reverses,
 *   regardless of scroll direction.
 *
 * How it works:
 *   1. Image initialised at SCALE_BASE (0.8) before any scroll.
 *   2. IntersectionObserver fires when section enters the viewport.
 *   3. targetScale is set to SCALE_MAX (1.05) and the rAF loop starts.
 *   4. Each frame: currentScale lerps toward targetScale.
 *   5. When the gap between current and target is < COMPLETION_THRESHOLD
 *      the loop stops, will-change is removed, and the observer is
 *      disconnected — nothing runs until the user hovers.
 *
 * Hover (desktop fine-pointer only):
 *   mouseenter  → targetScale = SCALE_MAX + SCALE_HOVER_DELTA, restart loop
 *   mouseleave  → targetScale = SCALE_MAX, restart loop
 *   The lerp blends both transitions smoothly.
 *
 * Performance:
 *   - Only transform: scale() — no layout reflow.
 *   - will-change: transform active only during animation.
 *   - rAF loop stops as soon as animation completes.
 *   - Observer is disconnected after first trigger.
 *   - Fully skipped for prefers-reduced-motion users.
 */

if (!customElements.get('cinematic-banner')) {
  customElements.define(
    'cinematic-banner',
    class CinematicBanner extends HTMLElement {
      /** @type {HTMLImageElement|null} */
      image = null;

      /** @type {number|null} requestAnimationFrame id */
      rafId = null;

      /** @type {IntersectionObserver|null} */
      observer = null;

      /** True once the entry animation has been triggered */
      hasPlayed = false;

      /** Whether the user is hovering (desktop only) */
      isHovered = false;

      /** Interpolated scale applied each frame */
      currentScale = 0.8;

      /** Scale the lerp moves toward */
      targetScale = 0.8;

      /* ── Config ────────────────────────────────────────────────────────── */

      /** Starting scale — slightly zoomed out */
      SCALE_BASE = 0.8;

      /** Final resting scale after animation completes */
      SCALE_MAX = 1;

      /** Extra scale added while hovering (desktop) */
      SCALE_HOVER_DELTA = 0.04;

      /**
       * Lerp speed per frame (0–1).
       * 0.045 = slow, cinematic ease — image settles gradually into place.
       */
      LERP_SPEED = 0.045;

      /**
       * Stop the loop once the gap between current and target is below this.
       * Prevents infinite micro-updates.
       */
      COMPLETION_THRESHOLD = 0.0005;

      /* ── Lifecycle ─────────────────────────────────────────────────────── */

      connectedCallback() {
        this.image = this.querySelector('.cinematic-banner__image');
        if (!this.image) return;

        // Skip everything for users who prefer reduced motion.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        // Set the initial zoomed-out scale before the section enters view.
        this.image.style.setProperty('--cinematic-scale', this.currentScale.toFixed(4));

        this.setupObserver();
        this.setupHover();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.stopLoop();
        this.removeEventListener('mouseenter', this.onMouseEnter);
        this.removeEventListener('mouseleave', this.onMouseLeave);
      }

      /* ── Intersection Observer ─────────────────────────────────────────── */

      setupObserver() {
        this.observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting || this.hasPlayed) continue;

              // Mark as played immediately so re-entry never re-triggers.
              this.hasPlayed = true;

              this.image.style.willChange = 'transform';
              this.targetScale = this.SCALE_MAX;
              this.startLoop();

              // Disconnect — we only need one trigger.
              this.observer.disconnect();
            }
          },
          { threshold: 0 }
        );

        this.observer.observe(this);
      }

      /* ── Hover (fine-pointer / desktop only) ───────────────────────────── */

      setupHover() {
        if (!window.matchMedia('(pointer: fine)').matches) return;

        this.onMouseEnter = () => {
          if (!this.hasPlayed) return;
          this.isHovered = true;
          this.targetScale = this.SCALE_MAX + this.SCALE_HOVER_DELTA;
          this.image.style.willChange = 'transform';
          this.startLoop();
        };

        this.onMouseLeave = () => {
          this.isHovered = false;
          this.targetScale = this.SCALE_MAX;
          this.startLoop();
        };

        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);
      }

      /* ── rAF loop ──────────────────────────────────────────────────────── */

      tick() {
        this.currentScale += (this.targetScale - this.currentScale) * this.LERP_SPEED;

        this.image.style.setProperty('--cinematic-scale', this.currentScale.toFixed(4));

        if (Math.abs(this.targetScale - this.currentScale) < this.COMPLETION_THRESHOLD) {
          // Snap precisely to target and stop.
          this.currentScale = this.targetScale;
          this.image.style.setProperty('--cinematic-scale', this.currentScale.toFixed(4));

          // Release the GPU layer unless hover is keeping it active.
          if (!this.isHovered) {
            this.image.style.willChange = 'auto';
          }

          this.stopLoop();
          return;
        }

        this.rafId = requestAnimationFrame(this.tick.bind(this));
      }

      startLoop() {
        // If a loop is already running it will pick up the new targetScale
        // automatically — no need to restart.
        if (this.rafId !== null) return;
        this.tick();
      }

      stopLoop() {
        if (this.rafId === null) return;
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }
  );
}
